import request from "@/utils/request";

const basePath = "/api/mts-api/agent-service/v1/conversations";

// 仅正式会话浏览器接口使用服务端已按稳定码本地化的错误；聊天正文和翻译快照不走此标记。
const agentServiceRequest = (config) => {
  const { viewerRole, ...body } = config.data || {};
  return request({ ...config, ...(config.data ? { data: body } : {}),
    ...(viewerRole === 'RECEPTION' ? { headers: { ...config.headers, 'X-XSOU-Chat-Role': 'RECEPTION' } } : {}),
    agentServiceLocalizedError: true });
};

/** 供需归属由Spring从clueId读取，不向浏览器开放指定接待人或伪造商品。 */
export const openSupplyDemandConsultation = (data) => agentServiceRequest({
  url: '/api/mts-api/agent-service/v1/supply-demand/open', method: 'post', data, skipErrorMessage: true,
});

/** 列出当前登录用户作为客户或固定业务员参与的正式客服会话。 */
export function listAgentServiceConversations(limit = 50, { inbox = false } = {}) {
  return agentServiceRequest({
    url: inbox ? `${basePath}/inbox` : basePath,
    method: "get",
    params: { limit },
  });
}

/** 按商品编码或订单号解析并复用正式客服会话。 */
export function resolveAgentServiceConversation(data) {
  return agentServiceRequest({
    url: `${basePath}/resolve`,
    method: "post",
    data,
  });
}

/** 读取当前登录参与者可访问的会话详情。 */
export function getAgentServiceConversation(conversationId) {
  return agentServiceRequest({
    url: `${basePath}/${conversationId}`,
    method: "get",
  });
}

/** 按服务端sequence分页读取会话消息。 */
export function getAgentServiceMessages(
  conversationId,
  { beforeSequence, limit = 30, includeAgentStatus = false } = {}
) {
  return agentServiceRequest({
    url: `${basePath}/${conversationId}/messages`,
    method: "get",
    ...(includeAgentStatus ? { headers: { 'X-XSOU-Agent-Status': 'true' } } : {}),
    params: {
      ...(beforeSequence ? { beforeSequence } : {}),
      limit,
    },
  });
}

/** 由Spring重新验证失败终态，幂等写入网络繁忙系统通知；不由浏览器伪造AI回复。 */
export function acknowledgeAgentServiceFailure(conversationId, businessMessageId) {
  return agentServiceRequest({ url: `${basePath}/${conversationId}/agent-failure-notice`, method: 'post', data: { businessMessageId } });
}

/** 使用浏览器幂等ID发送一条客户或真人业务员消息。 */
export function sendAgentServiceMessage(conversationId, data) {
  // 用可选请求头携带发送版本，旧MTS会忽略此头，不能因正文新增字段而使旧版聊天全部失败。
  const { expectedControlVersion, ...body } = data;
  return agentServiceRequest({
    url: `${basePath}/${conversationId}/messages`,
    method: "post",
    data: body,
    ...(expectedControlVersion ? { headers: { 'X-XSOU-Expected-Control-Version': String(expectedControlVersion) } } : {}),
  });
}

/** 推进当前登录参与者自己的已读游标。 */
export function updateAgentServiceReadCursor(conversationId, sequence, viewerRole = '') {
  return agentServiceRequest({
    url: `${basePath}/${conversationId}/read-cursor`,
    method: "put",
    data: { lastReadSequence: sequence, ...(viewerRole === 'RECEPTION' ? { viewerRole } : {}) },
  });
}

const changeAgentServiceControl = (conversationId, action, data) =>
  agentServiceRequest({
    url: `${basePath}/${conversationId}/handoff/${action}`,
    method: "post",
    data,
  });

/** 由客户请求负责业务员人工接管。 */
export function requestAgentServiceHuman(conversationId, data) {
  return changeAgentServiceControl(conversationId, "request", data);
}

/** 由会话固定业务员接受人工接管。 */
export function acceptAgentServiceHuman(conversationId, data) {
  return changeAgentServiceControl(conversationId, "accept", data);
}

/** 由会话固定业务员把控制权交回AI助手。 */
export function releaseAgentServiceToAi(conversationId, data) {
  return changeAgentServiceControl(conversationId, "release", data);
}

/** 由负责业务员拒绝本次人工接管请求，会话继续由AI回复。 */
export function rejectAgentServiceHandoff(conversationId, data) {
  return changeAgentServiceControl(conversationId, "reject", data);
}

const changeAgentServiceAi = (conversationId, action, data) =>
  agentServiceRequest({
    url: `${basePath}/${conversationId}/ai/${action}`,
    method: "post",
    data,
  });

/** 由负责业务员关闭AI代聊。 */
export function disableAgentServiceAi(conversationId, data) {
  return changeAgentServiceAi(conversationId, "disable", data);
}

/** 由客户选择Agent模式，或由负责业务员重新开启AI代聊。 */
export function enableAgentServiceAi(conversationId, data) {
  return changeAgentServiceAi(conversationId, "enable", data);
}

/** 仅取消当前版本的生成，不关闭会话、不转人工、不删除已持久消息。 */
export function cancelAgentServiceReply(conversationId, data) {
  return agentServiceRequest({ url: `${basePath}/${conversationId}/ai/cancel`, method: 'post', data });
}

/** 关闭当前客服会话。 */
export function closeAgentServiceConversation(conversationId, data) {
  return agentServiceRequest({
    url: `${basePath}/${conversationId}/close`,
    method: "post",
    data,
  });
}

/** 删除双方的MySQL业务会话；Agent运行链由Platform异步清理。 */
export function deleteAgentServiceConversation(conversationId) {
  return agentServiceRequest({
    url: `${basePath}/${conversationId}`,
    method: "delete",
  });
}

/** 只清除Agent形成的咨询记忆；聊天记录和业务会话继续保留。 */
export function forgetAgentServiceConversationMemory(conversationId) {
  return agentServiceRequest({
    url: `${basePath}/${conversationId}/memory/forget`,
    method: "post",
  });
}
